import React, { useMemo, useRef, useCallback, useState } from 'react';
import BalanceFanChart from './charts/BalanceFanChart';
import IncomeBreakdownChart from './charts/IncomeBreakdownChart';
import AssetClassChart from './charts/AssetClassChart';
import WithdrawalRateChart from './charts/WithdrawalRateChart';
import HouseholdWealthChart from './charts/HouseholdWealthChart';
import PortfolioBreakdownChart from './charts/PortfolioBreakdownChart';
import PortfolioAllocationSummary from './PortfolioAllocationSummary';
import SaveScenarioModal from './SaveScenarioModal';
import { formatCurrency } from '../utils/formatters';
import ManagedDisclaimer from '../../../components/ManagedDisclaimer';

const CURRENT_YEAR = new Date().getFullYear();

const COLOURS = {
  primary: '#072040',
  secondary: '#f0a500',
  success: '#91CF9B',
  info: '#599BE3',
  light: '#E7E6E6',
  white: '#ffffff',
};

// Helper to get age_1 from formData (handles both old age_1 and new birth_year format) or results
const getAge1 = (formData, results) => {
  if (results?.input_age_1) return results.input_age_1;
  // Support old format (age_1 directly stored)
  if (formData?.age_1) return formData.age_1;
  // Support new format (birth years)
  if (formData?.birth_year_1) {
    const birthYear1 = formData.birth_year_1;
    const birthYear2 = formData.birth_year_2 || birthYear1;
    const age1Calc = CURRENT_YEAR - birthYear1;
    const age2Calc = CURRENT_YEAR - birthYear2;
    const isCouple = formData.couple === 'Yes';
    return isCouple ? Math.max(age1Calc, age2Calc) : age1Calc;
  }
  return 67;
};

const PERCENTILE_OPTIONS = [
  { value: 0, label: 'Pessimistic' },
  { value: 1, label: 'Conservative' },
  { value: 2, label: '50th Percentile' },
  { value: 3, label: 'Favourable' },
  { value: 4, label: 'Optimistic' },
];

const PERCENTILE_TOOLTIPS = {
  0: '10th percentile \u2014 9 times out of 10 your actual journey should be better',
  1: '25th percentile \u2014 3 times out of 4 your journey should be better',
  2: 'Median scenario \u2014 like a coin flip, equally likely to be better or worse',
  3: '75th percentile \u2014 only 1 time out of 4 would your journey be better',
  4: '90th percentile \u2014 only 1 time out of 10 would your journey be better',
};

const ResultsDashboard = ({
  results,
  formData,
  selectedPercentileIndex,
  setSelectedPercentileIndex,
  onExportPdf,
  frozenScenarios,
  portfolioRegistry,
  // Database save/load props
  savedScenarioId,
  isSaving,
  loadedClientIdentifier,
  isScenarioLocked,
  onSaveToDatabase,
  onOpenLoadModal,
}) => {
  const balanceRef = useRef(null);
  const incomeRef = useRef(null);
  const householdWealthRef = useRef(null);
  const assetClassRef = useRef(null);
  const portfolioBreakdownRef = useRef(null);
  const withdrawalRateRef = useRef(null);

  const [showClientModal, setShowClientModal] = useState(false);
  const [clientName, setClientName] = useState('');

  // Database modal states
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleDownloadClick = () => {
    setShowClientModal(true);
  };

  const handleExportConfirm = () => {
    setShowClientModal(false);
    onExportPdf(captureChartImages(), clientName || 'Client');
  };

  const handleSaveToDatabase = async (clientIdentifier, scenarioName) => {
    const result = await onSaveToDatabase(clientIdentifier, scenarioName);
    if (result) {
      setShowSaveModal(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const captureChartImages = useCallback(() => {
    const images = {};
    const refMap = {
      balance_chart: balanceRef,
      income_chart: incomeRef,
      household_wealth_chart: householdWealthRef,
      asset_class_chart: assetClassRef,
      portfolio_breakdown_chart: portfolioBreakdownRef,
      withdrawal_rate_chart: withdrawalRateRef,
    };
    for (const [key, ref] of Object.entries(refMap)) {
      try {
        if (ref.current && ref.current.toBase64Image) {
          images[key] = ref.current.toBase64Image();
        }
      } catch (e) {
        console.warn(`Failed to capture ${key}:`, e);
      }
    }
    return images;
  }, []);

  const incomeBreakdownData = useMemo(() => {
    if (!results) return null;

    const idx = selectedPercentileIndex;
    const portfolioIncome = results.portfolio_income_percentiles?.[idx] || [];
    const pensionIncome = results.pension_income_percentiles?.[idx] || [];
    const incomeStream = results.income_stream_percentiles?.[idx] || [];
    const otherIncomeTotal = results.other_income_percentiles?.[idx] || [];
    const lumpSumIncome = results.lump_sum_percentiles?.[idx] || [];

    const employmentAnnual = results.input_employment_income || 0;
    const otherAnnual = results.input_other_income || 0;
    const rentalAnnual = results.input_net_rental_income || 0;
    const workYears = results.input_work_years || 0;
    const otherIncomeYears = results.input_other_income_years || 0;
    const rentalIncomeYears = results.input_rental_income_years || 0;

    const years = results.years || [];

    const employmentIncome = years.map((y) => (y < workYears ? employmentAnnual : 0));
    const otherOnlyIncome = years.map((y) => (y < otherIncomeYears ? otherAnnual : 0));
    const rentalIncome = years.map((y) => (y < rentalIncomeYears ? rentalAnnual : 0));

    return {
      portfolio_income: portfolioIncome,
      pension_income: pensionIncome,
      employment_income: employmentIncome,
      other_income: otherOnlyIncome,
      rental_income: rentalIncome,
      income_stream: incomeStream,
      lump_sum_income: lumpSumIncome,
    };
  }, [results, selectedPercentileIndex]);

  const householdWealthData = useMemo(() => {
    if (!results) return null;

    const years = results.years || [];
    const portfolioBalance = results.balance_percentiles?.[2] || [];

    // Use the per-year array from backend (accounts for property sale), fall back to flat scalar
    const investmentProperty = results.investment_property_value_array
      || years.map(() => results.input_investment_property_value || 0);

    const nonFinValue = results.input_non_financial_assets || 0;
    const nonFinancialAssets = years.map(() => nonFinValue);

    const annuityPurchaseAmount = results.input_annuity_purchase_amount || 0;
    const age1 = getAge1(formData, results);
    const inflationRate = 0.025;
    const annuityValue = years.map((y) => {
      if (annuityPurchaseAmount <= 0) return 0;
      const currentAge = age1 + y;
      if (currentAge >= 86) return 0;
      const yearsRemaining = 86 - currentAge;
      const totalAnnuityYears = 86 - age1;
      if (totalAnnuityYears <= 0) return 0;
      // Linear decrease deflated by inflation (model runs in real terms)
      const linearValue = annuityPurchaseAmount * (yearsRemaining / totalAnnuityYears);
      return linearValue / Math.pow(1 + inflationRate, y);
    });

    return { portfolioBalance, investmentProperty, annuityValue, nonFinancialAssets };
  }, [results, formData]);

  if (!results) return null;

  const age1 = getAge1(formData, results);
  const years = results.years || [];

  return (
    <div className="results-dashboard">
      {/* Main Retirement Results Card */}
      <div className="card">
        <div className="card-header results-header">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h5 className="mb-0">Retirement Results</h5>
              <p className="small mb-0 mt-2">
                <em>All results are shown in today's dollars (adjusted for inflation).</em>
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={onOpenLoadModal}
                title="Load a saved scenario"
              >
                <i className="bi bi-cloud-download me-1"></i>
                Load Saved
              </button>
              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                onClick={() => setShowSaveModal(true)}
                title="Save this scenario to the database"
              >
                <i className="bi bi-cloud-upload me-1"></i>
                Save to Database
              </button>
              {saveSuccess && (
                <span className="badge bg-success d-flex align-items-center">
                  <i className="bi bi-check-circle me-1"></i>
                  Saved!
                </span>
              )}
            </div>
          </div>
          {loadedClientIdentifier && (
            <div className="mt-2">
              <span className="badge bg-info">
                <i className="bi bi-person me-1"></i>
                Client: {loadedClientIdentifier}
              </span>
              {savedScenarioId && isScenarioLocked && (
                <span className="badge bg-secondary ms-2">
                  <i className="bi bi-cloud-check me-1"></i>
                  Saved (ID: {savedScenarioId})
                </span>
              )}
              {!isScenarioLocked && !savedScenarioId && (
                <span className="badge bg-warning text-dark ms-2">
                  <i className="bi bi-pencil me-1"></i>
                  Editing — will save as new scenario
                </span>
              )}
            </div>
          )}
        </div>

        <div className="card-body">
          {/* Retirement Projection card with percentile dropdown */}
          <div className="card mb-3">
            <div className="card-header projection-subheader d-flex justify-content-between align-items-center">
              <h5 className="mb-0" style={{ color: COLOURS.primary }}>Retirement Projection</h5>
              <div style={{ width: '300px' }}>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <label htmlFor="percentileSelect" className="form-label small mb-0">
                    Income Chart Scenario:
                  </label>
                  <i
                    className="bi bi-info-circle text-muted"
                    title={Object.values(PERCENTILE_TOOLTIPS).join('\n')}
                    style={{ cursor: 'help' }}
                  ></i>
                </div>
                <select
                  className="form-select"
                  id="percentileSelect"
                  value={selectedPercentileIndex}
                  onChange={(e) => setSelectedPercentileIndex(Number(e.target.value))}
                >
                  {PERCENTILE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="card-body">
              {/* Row 1: Balance Fan Chart | Income Breakdown */}
              <div className="row">
                <div className="col-md-6">
                  <div className="chart-container">
                    <BalanceFanChart
                      ref={balanceRef}
                      percentileData={results.balance_percentiles}
                      years={years}
                      percentiles={results.percentiles}
                      age1={age1}
                    />
                  </div>
                  <p className="small chart-info-text mt-2">
                    <i className="bi bi-info-circle me-1"></i> This fan chart shows the balance percentile at each point in time, rather than individual percentile paths.
                  </p>
                </div>
                <div className="col-md-6">
                  <div className="chart-container">
                    {incomeBreakdownData && (
                      <IncomeBreakdownChart
                        ref={incomeRef}
                        data={incomeBreakdownData}
                        years={years}
                        age1={age1}
                      />
                    )}
                  </div>
                  <p className="small chart-info-text mt-2">
                    <i className="bi bi-info-circle me-1"></i> This chart shows the income breakdown inferred from the balance percentile on the left.
                  </p>
                </div>
              </div>

              {/* Row 2: Household Wealth | Asset Class Allocation */}
              <div className="row mt-4">
                <div className="col-md-6">
                  <div className="chart-container">
                    {householdWealthData && (
                      <HouseholdWealthChart
                        ref={householdWealthRef}
                        portfolioBalance={householdWealthData.portfolioBalance}
                        investmentProperty={householdWealthData.investmentProperty}
                        annuityValue={householdWealthData.annuityValue}
                        nonFinancialAssets={householdWealthData.nonFinancialAssets}
                        years={years}
                        age1={age1}
                      />
                    )}
                  </div>
                  <p className="small chart-info-text mt-2">
                    <i className="bi bi-info-circle me-1"></i> Household wealth over time: portfolio balance (50th percentile), investment property (held during rental period, then sold), annuity value (linear decrease to $0 at age 86, deflated by inflation), and non-financial assets (constant in real terms).
                  </p>
                </div>
                <div className="col-md-6">
                  <div className="chart-container">
                    {results.asset_class_allocation && (
                      <AssetClassChart ref={assetClassRef} allocation={results.asset_class_allocation} />
                    )}
                  </div>
                  <p className="small chart-info-text mt-2">
                    <i className="bi bi-info-circle me-1"></i> This chart shows asset class allocation breakdown through portfolio lookthrough.
                  </p>
                </div>
              </div>

              {/* Row 3: Portfolio Breakdown | Withdrawal Rate */}
              <div className="row mt-4">
                <div className="col-md-6">
                  <div className="chart-container">
                    <PortfolioBreakdownChart
                      ref={portfolioBreakdownRef}
                      portfolioIncome={results.portfolio_income_percentiles?.[2] || []}
                      balance={results.balance_percentiles?.[2] || []}
                      yieldRate={results.portfolio_yield_rate || 0}
                      totalReturn={results.calculated_return || 0}
                      years={years}
                      age1={age1}
                    />
                  </div>
                  <p className="small chart-info-text mt-2">
                    <i className="bi bi-info-circle me-1"></i> This chart shows the breakdown of portfolio income into yield and portfolio drawdown (50th percentile only). It does not include age pension, other, or annuity income.
                  </p>
                </div>
                <div className="col-md-6">
                  <div className="chart-container">
                    <WithdrawalRateChart
                      ref={withdrawalRateRef}
                      portfolioIncome={results.portfolio_income_percentiles?.[2] || []}
                      balance={results.balance_percentiles?.[2] || []}
                      years={years}
                      age1={age1}
                      initialBalance={results.input_financial_assets || 0}
                    />
                  </div>
                  <p className="small chart-info-text mt-2">
                    <i className="bi bi-info-circle me-1"></i> Withdrawal rate is calculated as portfolio income drawn &divide; start-of-year portfolio balance (50th percentile). Rates of 100% can repeat because investment returns create residual balances that are then fully drawn the following year.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Allocation Summary */}
          <PortfolioAllocationSummary
            weights={results.portfolio_weights}
            yieldRate={results.portfolio_yield_rate}
            growthExposure={results.portfolio_growth}
            expectedReturn={results.calculated_return}
            volatility={results.calculated_volatility}
            portfolioRegistry={portfolioRegistry}
          />

          {/* Summary HTML Section */}
          {results.summary && (
            <div className="card mb-3">
              <div id="summary-content" className="mt-3 p-3">
                <div dangerouslySetInnerHTML={{ __html: results.summary }} />
              </div>
            </div>
          )}
        </div>

        {/* Card Footer: Download Report only */}
        {onExportPdf && (
        <div className="card-footer d-flex justify-content-end">
          <button
            className="btn btn-retirement-primary"
            onClick={handleDownloadClick}
          >
            Download Report
          </button>
        </div>
        )}
      </div>

      {/* Client Name Modal */}
      {showClientModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: COLOURS.primary, color: COLOURS.white }}>
                <h5 className="modal-title">Download Report</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowClientModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <label htmlFor="clientNameInput" className="form-label fw-semibold">
                  Client Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="clientNameInput"
                  placeholder="Enter client name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleExportConfirm();
                  }}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowClientModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-retirement-primary"
                  onClick={handleExportConfirm}
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Frozen Scenarios Comparison */}
      {frozenScenarios && frozenScenarios.length > 0 && (
        <div className="mt-3 mb-4">
          <div className="card shadow-sm" style={{ borderTop: `4px solid ${COLOURS.info}` }}>
            <div className="card-header results-header">
              <h6 className="mb-0 fw-bold">Scenario Comparison</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm table-hover scenario-table mb-0">
                  <thead>
                    <tr>
                      <th>Scenario</th>
                      <th className="text-end">Success</th>
                      <th className="text-end">Return</th>
                      <th className="text-end">Volatility</th>
                      <th className="text-end">Growth</th>
                      <th className="text-end">Starting Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ backgroundColor: 'rgba(0, 51, 102, 0.05)' }}>
                      <td className="fw-bold" style={{ color: COLOURS.primary }}>Current</td>
                      <td className="text-end fw-bold">{results.probability?.success?.toFixed(1)}%</td>
                      <td className="text-end">{(results.calculated_return * 100).toFixed(1)}%</td>
                      <td className="text-end">{(results.calculated_volatility * 100).toFixed(1)}%</td>
                      <td className="text-end">{(results.portfolio_growth * 100).toFixed(1)}%</td>
                      <td className="text-end">{formatCurrency(formData?.financial_assets)}</td>
                    </tr>
                    {frozenScenarios.map((s, idx) => (
                      <tr key={idx}>
                        <td>{s.name}</td>
                        <td className="text-end fw-bold">
                          {s.results?.probability?.success?.toFixed(1)}%
                        </td>
                        <td className="text-end">
                          {((s.results?.calculated_return || 0) * 100).toFixed(1)}%
                        </td>
                        <td className="text-end">
                          {((s.results?.calculated_volatility || 0) * 100).toFixed(1)}%
                        </td>
                        <td className="text-end">
                          {((s.results?.portfolio_growth || 0) * 100).toFixed(1)}%
                        </td>
                        <td className="text-end">
                          {formatCurrency(s.formData?.financial_assets)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer Card */}
      <div className="card disclaimer-card mt-3">
        <div className="card-body">
          <div className="disclaimer-text">
            <h6><i className="bi bi-info-circle"></i> Retirement Calculator &ndash; Important Information</h6>
            <ManagedDisclaimer dkey="tool_retirement_card" as="p" className="small text-muted mb-0" />
          </div>
        </div>
      </div>

      {/* Save Scenario Modal */}
      <SaveScenarioModal
        show={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveToDatabase}
        isSaving={isSaving}
        initialClientIdentifier={loadedClientIdentifier || ''}
      />

    </div>
  );
};

export default ResultsDashboard;
